import { useState } from 'react';
import { Link } from 'react-router-dom';
import PlatformVersionBadge from '@/components/feature/PlatformVersionBadge';
import type { ZifekUser } from '@/hooks/useAuth';

export type ClientSidebarTab =
  | 'overview'
  | 'etudes'
  | 'profile'
  | 'store'
  | 'wallet'
  | 'favorites'
  | 'chat'
  | 'messages'
  | 'orders'
  | 'courses'
  | 'payments'
  | 'besoins'
  | 'missions'
  | 'revenues'
  | 'packs'
  | 'appstore'
  | 'mes-applis'
  | 'outfits'
  | 'closet'
  | 'files'
  | 'documents'
  | 'downloads';

interface Props {
  user: ZifekUser;
  brandName: string;
  activeTab: string;
  fileCount: number;
  upcomingCount: number;
  onLogout: () => void;
}

interface NavItem {
  id: ClientSidebarTab;
  icon: string;
  label: string;
}

/**
 * Barre latérale du compte client (desktop).
 * Chaque entrée est un VRAI lien vers une page dédiée (/mon-compte/<onglet>),
 * ce qui permet d'ouvrir la section dans un nouvel onglet et de la partager.
 * Repliable : un bouton permet de la réduire aux icônes.
 */
export default function ClientSidebar({
  user,
  brandName,
  activeTab,
  fileCount,
  upcomingCount,
  onLogout,
}: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const accountItems: NavItem[] = [
    { id: 'overview', icon: 'ri-dashboard-line', label: 'Tableau de bord' },
    { id: 'etudes', icon: 'ri-book-open-line', label: "Mon espace d'études" },
    { id: 'appstore', icon: 'ri-store-3-line', label: 'App Store' },
    { id: 'mes-applis', icon: 'ri-apps-2-line', label: 'Mes applications' },
    { id: 'profile', icon: 'ri-user-settings-line', label: 'Mon profil' },
    { id: 'store', icon: 'ri-store-2-line', label: 'Mes soldes magasin' },
    { id: 'wallet', icon: 'ri-wallet-3-line', label: 'Mon portefeuille' },
    { id: 'favorites', icon: 'ri-heart-3-line', label: 'Mes favoris' },
    { id: 'chat', icon: 'ri-robot-2-line', label: 'Chat IA' },
    { id: 'messages', icon: 'ri-chat-smile-3-line', label: 'Messages' },
    { id: 'orders', icon: 'ri-shopping-bag-3-line', label: 'Mes commandes' },
    { id: 'courses', icon: 'ri-graduation-cap-line', label: 'Mes formations' },
    { id: 'payments', icon: 'ri-receipt-line', label: 'Historique des paiements' },
    { id: 'besoins', icon: 'ri-hand-heart-line', label: 'Besoins' },
    { id: 'missions', icon: 'ri-megaphone-line', label: 'Missions' },
    { id: 'packs', icon: 'ri-luggage-cart-line', label: 'Mes packs' },
    { id: 'revenues', icon: 'ri-money-dollar-circle-line', label: 'Mes gains créateur' },
  ];

  const spaceItems: NavItem[] = [
    { id: 'outfits', icon: 'ri-shirt-line', label: 'Mon Dressing' },
    { id: 'closet', icon: 'ri-t-shirt-line', label: 'Mon Armoire' },
    { id: 'documents', icon: 'ri-file-text-line', label: 'Mes documents' },
    { id: 'files', icon: 'ri-folder-3-line', label: 'Mes fichiers' },
    { id: 'downloads', icon: 'ri-download-cloud-2-line', label: 'Mes téléchargements' },
  ];

  const sectionLabel = (text: string) => {
    if (collapsed) {
      return <div className="mx-4 my-2 border-t border-background-200/50" />;
    }
    return (
      <div className="px-5 mb-1">
        <span className="text-[10px] font-semibold text-foreground-400 tracking-[0.12em] uppercase">{text}</span>
      </div>
    );
  };

  const renderItem = (item: NavItem, badge?: number) => {
    const active = activeTab === item.id;
    return (
      <Link
        key={item.id}
        to={`/mon-compte/${item.id}`}
        title={collapsed ? item.label : undefined}
        className={`group flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-l-[3px] no-underline ${
          active
            ? 'bg-foreground-100/60 text-foreground-900 border-l-foreground-900'
            : 'text-foreground-500 border-l-transparent hover:text-foreground-700 hover:bg-background-100 hover:border-l-foreground-200'
        }`}
      >
        <i className={`${item.icon} text-base flex-shrink-0 ${active ? 'text-foreground-800' : ''}`}></i>
        {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
        {!collapsed && badge != null && badge > 0 && (
          <span className="text-[11px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded-full">{badge}</span>
        )}
      </Link>
    );
  };

  return (
    <aside
      className={`zifek-account-sidebar flex-col shrink-0 self-start sticky top-14 md:top-16 z-20 h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)] ${
        collapsed ? 'w-[76px]' : 'w-60 lg:w-64'
      } border-r border-background-200/50 bg-background-50 transition-[width] duration-300 overflow-hidden`}
    >
      {/* Carte utilisateur */}
      <div className={`pt-6 pb-4 ${collapsed ? 'px-3' : 'px-4'}`}>
        <div
          className={`flex items-center ${
            collapsed ? 'justify-center' : 'gap-3'
          } p-3 bg-background-50 border border-background-200/40 rounded-xl`}
        >
          <div className="w-10 h-10 rounded-full bg-foreground-100 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-background-50">
            {user.image ? (
              <img src={user.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-foreground-600 font-bold text-sm">{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate">{user.name}</p>
              <p className="text-[11px] text-foreground-500 truncate">@{user.user_name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Bouton replier / déplier */}
      <div className={`mb-2 ${collapsed ? 'px-3' : 'px-4'}`}>
        <button
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Déplier le menu' : 'Replier le menu'}
          className={`w-full flex items-center ${
            collapsed ? 'justify-center' : 'gap-2'
          } px-3 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer`}
        >
          <i className={`${collapsed ? 'ri-arrow-right-double-line' : 'ri-arrow-left-double-line'} text-base flex-shrink-0`}></i>
          {!collapsed && <span>Replier le menu</span>}
        </button>
      </div>

      {/* Sections (défilement si manque de place) */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-2 sidebar-scroll">
        {sectionLabel('Compte')}
        <div className={`flex flex-col gap-0.5 mb-3 ${collapsed ? 'px-2' : 'px-3'}`}>
          {accountItems.map((item) => renderItem(item))}
        </div>

        {sectionLabel('Espace')}
        <div className={`flex flex-col gap-0.5 mb-3 ${collapsed ? 'px-2' : 'px-3'}`}>
          {spaceItems.map((item) => renderItem(item, item.id === 'files' ? fileCount : undefined))}
        </div>

        {sectionLabel('Agenda')}
        <div className={`flex flex-col gap-0.5 mb-3 ${collapsed ? 'px-2' : 'px-3'}`}>
          <Link
            to="/mon-planning"
            title={collapsed ? 'Planning semaine' : undefined}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap border-l-[3px] border-l-transparent hover:border-l-foreground-200 no-underline`}
          >
            <i className="ri-calendar-2-line text-base flex-shrink-0"></i>
            {!collapsed && <span className="flex-1 text-left">Planning semaine</span>}
            {!collapsed && upcomingCount > 0 && (
              <span className="ml-auto px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">
                {upcomingCount}
              </span>
            )}
          </Link>
          <Link
            to="/mon-compte/missions"
            title={collapsed ? 'Campagnes créateurs' : undefined}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap border-l-[3px] border-l-transparent hover:border-l-foreground-200 no-underline`}
          >
            <i className="ri-fire-line text-base flex-shrink-0"></i>
            {!collapsed && <span className="flex-1 text-left">Campagnes créateurs</span>}
          </Link>
        </div>
      </div>

      {/* Bas de la barre */}
      <div className={`shrink-0 py-3 border-t border-background-200/40 ${collapsed ? 'px-3' : 'px-4'}`}>
        <Link
          to="/"
          title={collapsed ? `Accueil ${brandName}` : undefined}
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer no-underline`}
        >
          <i className="ri-home-4-line text-sm flex-shrink-0"></i>
          {!collapsed && <span>Accueil {brandName}</span>}
        </Link>
        <button
          onClick={onLogout}
          title={collapsed ? 'Déconnexion' : undefined}
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-400 hover:text-red-600 hover:bg-red-50/60 transition-colors cursor-pointer w-full mt-0.5`}
        >
          <i className="ri-logout-box-line text-sm flex-shrink-0"></i>
          {!collapsed && <span>Déconnexion</span>}
        </button>
        <div className="pt-3 mt-1 flex justify-center">
          <PlatformVersionBadge />
        </div>
      </div>
    </aside>
  );
}